import { useState, useEffect } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";
import { updateFax } from "redux/slice/faxSlice";
import { onDownloadFax } from "../../Helper/DownloadFile";

import Table from "../Table";
import { format } from "date-fns";

import { Col, message, Row, Tag, Typography } from "antd";
import { Button, Modal } from "..";
import FaxDetailModalStyle, { ActionButton } from "./FaxDetailsModal.style";
import EditLgIcon from "components/Icons/EditIconLg";
import { SpanStyle } from "config/global.style";
import TextArea from "antd/es/input/TextArea";
import DownloadIcon from "components/Icons/Download";
import DeleteIcon from "components/Icons/Delete";
import SentFaxCardTable from "components/ui-component/modals/SentFaxCardTable";
import { getFaxStatusColor } from "components/Helper/faxStatusColor";

interface EditSentFaxModalProps {
  visible: boolean;
  onCancelClick?: any;
  data: any;
  setSentFaxData?: any;
  setDeleteId?: any;
}

const EditSentFaxModal = (props: EditSentFaxModalProps) => {
  const {
    visible = false,
    onCancelClick,
    data,
    setSentFaxData,
    setDeleteId,
  } = props;

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");

  const [tableData, setTableData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);

  const { user } = useAuth0();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setLoading(true);
    const faxLogs: any[] = data?.fax_log?.map((entry: string) => {
      const matchResult = entry.match(
        /\[(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} UTC)\]\[([^\]]+@[\w.]+)\]\[([^\]]+)\]\[([^\]]+)]/
      );

      if (matchResult) {
        const [timestamp, email, ipAddress, action] = matchResult.slice(1);
        return { timestamp, email, ipAddress, action };
      }

      return null;
    });

    // Filter out null entries before setting the table data
    const filteredFaxLogs = faxLogs?.length
      ? faxLogs?.filter((entry) => entry !== null)
      : [];
    setTableData(filteredFaxLogs);
    setLoading(false);
  }, [data]);

  const col = [
    {
      title: "Date",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (text: string, record: any) => (
        <div className="flex gap-2 items-center">
          {format(new Date(record.timestamp), "M-d-yyyy, hh:mm a")}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Ip Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
    },
  ];

  const onSubmit = async () => {
    setLoading(true);
    try {
      let payload = {
        id: data?.uuid,
        notes: note,
        read: "yes",
      };
      await dispatch(updateFax(payload)).unwrap();
      onCancelClick();
      setNote("");
      setIsEdit(false);
      message.success("Fax Edited successfully");
    } catch (error: any) {
      message.error("Fax can't be edited");
      console.log("Update fax err", error?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Fax Details</span>
          <div className="right-action d-flex gap-3-5 ">
            <ActionButton
              size="small"
              icon={<DownloadIcon />}
              onClick={() => {
                onDownloadFax(
                  data,
                  data?.download_link,
                  dispatch,
                  user,
                  navigation
                );
              }}
            />
            <ActionButton
              size="small"
              icon={<DeleteIcon />}
              className="delete-icon"
              onClick={() => {
                setDeleteId(data?.uuid);
                setSentFaxData(null);
              }}
            />
          </div>
        </div>
      }
      onCancel={() => {
        setNote("");
        setIsEdit(false);
        onCancelClick();
      }}
      width="900px"
      onOk={() => onSubmit()}
      okText="Submit"
      isLoading={loading}
    >
      <FaxDetailModalStyle>
        <div className="faxdata-top">
          <Row gutter={[20, 20]}>
            <Col xs={12} lg={8}>
              <div className="detailsblock">
                <div className="heading">Sender Fax Number</div>
                <p className="details">{data?.from_number ?? ""}</p>
              </div>
            </Col>
            <Col xs={12} lg={8}>
              <div className="detailsblock">
                <div className="heading">Date</div>
                <p className="details">{data?.created_on ?? ""}</p>
              </div>
            </Col>
            <Col xs={12} lg={8}>
              <div className="detailsblock">
                <div className="heading">Recipient Fax Number</div>
                <p className="details">{data?.to_number ?? ""}</p>
              </div>
            </Col>{" "}
            <Col xs={12} lg={8}>
              <div className="detailsblock">
                <div className="heading">No. of Pages</div>
                <p className="details">{data?.pages ?? 0}</p>
              </div>
            </Col>
            <Col xs={12} lg={8}>
              <div className="detailsblock">
                <div className="heading">Status</div>
                <p>
                  <Tag bordered={false} color={getFaxStatusColor(data?.status)}>
                    <span className="capitalize">{data?.status}</span>
                  </Tag>
                </p>
              </div>
            </Col>
          </Row>
        </div>
        <div className="edit-fax-wrapper">
          <div className="faxdata-bottom">
            <div className="faxdata-bottom__left">
              <Typography.Title level={5}>Notes</Typography.Title>
            </div>
            <div className="faxdata-bottom__right">
              <Button
                type="link"
                size="small"
                icon={<EditLgIcon />}
                onClick={() => {
                  !isEdit && setNote(data?.notes || "");
                  setIsEdit(!isEdit);
                }}
              ></Button>
            </div>
          </div>
          {isEdit ? (
            <TextArea
              placeholder="Enter a note"
              value={note}
              onChange={(e: any) => setNote(e.target.value)}
              className="my-10"
            />
          ) : (
            <SpanStyle className="spanstyle">{data?.notes}</SpanStyle>
          )}
        </div>

        {tableData?.length > 0 && (
          <div
            className="fax-detail-table-wrapper mobile-hide"
            style={{ maxHeight: "400px", overflow: "auto" }}
          >
            <Table
              data={tableData}
              columns={col}
              isLoading={loading}
              pagination={false}
              scroll={{ y: 400 }}
              pageSize={10}
            />
          </div>
        )}
        {
          <div className="mobile-table-block desktop-hide">
            <SentFaxCardTable sentFaxData={tableData} />
          </div>
        }
      </FaxDetailModalStyle>
    </Modal>
  );
};

export default EditSentFaxModal;
